import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useAuthenticatedApi } from "@/utils/callApi";
import EditUserForm from "@/components/EditUserForm";
import { BrandButton as Button, SearchableSelect, Toast, TrackUserDashboard } from "@/components";
import useToast from "@/components/useToast";
import { Spinner } from "react-bootstrap";

export default function ManageUsers() {
  const { secureHelpers, isLoading: apiLoading } = useAuthenticatedApi();
  const { toast, showToast, hideToast } = useToast();

  // Edit user state
  const [users, setUsers] = useState([]);
  const [hospices, setHospices] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [roles, setRoles] = useState([]);

  // Archive state
  const [archiveSearchText, setArchiveSearchText] = useState("");
  const [archiveUserId, setArchiveUserId] = useState("");
  const [archiveShowSuggestions, setArchiveShowSuggestions] = useState(false);
  const [archiveHighlightedIndex, setArchiveHighlightedIndex] = useState(-1);
  const [pendingDeleteUserId, setPendingDeleteUserId] = useState(null);
  const archiveSuggestionsRef = useRef(null);

  // Resend email state
  const [resendSearchText, setResendSearchText] = useState("");
  const [resendSelectedUserId, setResendSelectedUserId] = useState("");
  const [resendShowSuggestions, setResendShowSuggestions] = useState(false);
  const [resendHighlightedIndex, setResendHighlightedIndex] = useState(-1);
  const resendSuggestionsRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Track user state
  const [isTrackingUser, setIsTrackingUser] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    loadUsers();
    loadHospices();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await secureHelpers.get('/api/Members');
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load users:', error);
      showToast({ variant: 'error', message: 'Failed to load users' });
    }
  };

  const loadHospices = async () => {
    try {
      const data = await secureHelpers.get('/api/Hospices');
      const sortedData = Array.isArray(data)
        ? data.filter(h => h && h.name).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        : [];
      setHospices(sortedData);
    } catch (error) {
      console.error('Failed to load hospices:', error);
      showToast({ variant: 'error', message: 'Failed to load hospices' });
    }
  };

  const loadRoles = async () => {
    try {
      const data = await secureHelpers.get('/api/roles');
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load roles:', error);
      showToast({ variant: 'error', message: 'Failed to load roles' });
    }
  };

  // Save user changes via API
  const handleSaveUser = async (userData) => {
    try {
      const payload = {
        Id: userData.id,
        FirstName: userData.firstName,
        LastName: userData.lastName,
        Email: userData.email,
        HospiceRoles: userData.hospiceRoles || [],
        EnableBypassSurveyValidation: userData.enableBypassSurveyValidation || false,
        EnableMultiSurveyExperience: userData.enableMultiSurveyExperience || false
      };

      await secureHelpers.put(`/api/Members/${userData.id}`, payload);

      await loadUsers();

      const updatedUser = await secureHelpers.get(`/api/Members/${userData.id}`);
      setUsers(prevUsers =>
        prevUsers.map(u => u.id === updatedUser.id ? { ...u, hospiceRoles: updatedUser.hospiceRoles } : u)
      );

      setIsEditingUser(false);
      setSelectedUserId(null);
      showToast({ variant: 'success', message: 'User updated successfully' });
    } catch (error) {
      console.error('Failed to update user:', error);
      showToast({ variant: 'error', message: 'Failed to update user: ' + error.message });
    }
  };

  // Archive - filtered users for autocomplete
  const archiveFilteredUsers = useMemo(() => {
    const q = archiveSearchText.trim().toLowerCase();
    if (!q) return users.slice(0, 10);
    return users.filter(u => {
      const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      return fullName.includes(q) || (u.email || '').toLowerCase().includes(q) || (u.hospiceId || '').toLowerCase().includes(q);
    }).slice(0, 10);
  }, [archiveSearchText, users]);

  const onArchiveSuggestionClick = (user) => {
    console.log('Selected user to archive:', user.id, user.firstName, user.lastName);
    setArchiveUserId(String(user.id));
    setArchiveSearchText(`${user.firstName || ''} ${user.lastName || ''}`.trim());
    setArchiveShowSuggestions(false);
    setArchiveHighlightedIndex(-1);
  };

  const handleArchiveSearchKeyDown = (e) => {
    if (!archiveShowSuggestions) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setArchiveHighlightedIndex(i => Math.min(i + 1, archiveFilteredUsers.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setArchiveHighlightedIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (archiveHighlightedIndex >= 0) onArchiveSuggestionClick(archiveFilteredUsers[archiveHighlightedIndex]);
      else if (archiveFilteredUsers.length > 0) onArchiveSuggestionClick(archiveFilteredUsers[0]);
    } else if (e.key === 'Escape') { setArchiveShowSuggestions(false); setArchiveHighlightedIndex(-1); }
  };

  const startDelete = (userId) => {
    if (userId == null) return;
    console.log('Starting delete for user:', userId);
    setPendingDeleteUserId(String(userId));
  };

  const performDelete = async (userId) => {
    if (userId == null) return;
    try {
      await secureHelpers.delete(`/api/Members/${userId}`);
      setUsers(prev => prev.filter(u => String(u.id) !== String(userId)));
      if (String(userId) === String(archiveUserId)) setArchiveUserId("");
      setPendingDeleteUserId(null);
      setArchiveSearchText("");
      setArchiveShowSuggestions(false);
      setArchiveHighlightedIndex(-1);
      showToast({ variant: 'success', message: 'User archived' });
    } catch (error) {
      console.error('Failed to archive user:', error);
      showToast({ variant: 'error', message: 'Failed to archive user' });
    }
  };

  // Resend email - filtered users for autocomplete
  const resendFilteredUsers = useMemo(() => {
    const q = resendSearchText.trim().toLowerCase();
    if (!q) return users.slice(0, 10);
    return users.filter(u => {
      const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      return fullName.includes(q) || (u.email || '').toLowerCase().includes(q) || (u.hospiceId || '').toLowerCase().includes(q);
    }).slice(0, 10);
  }, [resendSearchText, users]);

  const onResendSuggestionClick = (user) => {
    setResendSelectedUserId(String(user.id));
    setResendSearchText(`${user.firstName || ''} ${user.lastName || ''}`.trim());
    setResendShowSuggestions(false);
    setResendHighlightedIndex(-1);
  };

  const handleResendSearchKeyDown = (e) => {
    if (!resendShowSuggestions) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setResendHighlightedIndex(i => Math.min(i + 1, resendFilteredUsers.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setResendHighlightedIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (resendHighlightedIndex >= 0) onResendSuggestionClick(resendFilteredUsers[resendHighlightedIndex]);
      else if (resendFilteredUsers.length > 0) onResendSuggestionClick(resendFilteredUsers[0]);
    } else if (e.key === 'Escape') { setResendShowSuggestions(false); setResendHighlightedIndex(-1); }
  };

  const resendNewUserEmail = async () => {
    if (!resendSelectedUserId) {
      showToast({ variant: 'error', message: 'Please select a user to resend the new user email.' });
      return;
    }
    setIsSaving(true);
    try {
      await secureHelpers.post(`/api/Members/${resendSelectedUserId}/resendConfirmationEmail`);
      showToast({ variant: 'success', message: 'New user email resent successfully.' });
      setResendSelectedUserId("");
      setResendSearchText("");
    } catch (error) {
      console.error(error);
      showToast({ variant: 'error', message: error.message || 'Failed to resend new user email.' });
    } finally {
      setIsSaving(false);
    }
  };

   const handleChildLoaded = useCallback(() => {
    setLoading(false);
  }, []);

const selectedUser = users.find(u => String(u.id) === String(selectedUserId)) || null;
const archiveSelectedUser = users.find(u => String(u.id) === String(archiveUserId)) || null;

  return (
    <div className="container-fluid py-4">
      <h1 style={{ color: "#F16D09", marginBottom: "2rem" }}>Manage Users & Settings</h1>

      <Toast {...toast} onClose={hideToast} />

      {/* Edit User */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="card p-3 h-100">
            <h4 style={{ marginTop: 0, color: "#F16D09" }}>Edit User</h4>
            
            {!isEditingUser ? (
              <>
              <p className="text-muted">Select a user from the list to edit their details.</p>
                <div className="mb-3">
                  <label style={{ color: "#2e1719" }} className="form-label">Select User:</label>
                  <SearchableSelect
                    options={users}
                    value={selectedUserId || ''}
                    onChange={(userId) => setSelectedUserId(userId)}
                    placeholder="Search or select..."
                    getLabel={(user) => `${user.firstName} ${user.lastName} (${user.email})`}
                    getValue={(user) => user.id}
                  />
                </div>

                <Button
                  onClick={async () => {
                    try {
                      const fullUserData = await secureHelpers.get(`/api/Members/${selectedUserId}`);
                      setUsers(prevUsers =>
                        prevUsers.map(u => u.id === selectedUserId ? {
                          ...u,
                          hospiceRoles: fullUserData.hospiceRoles,
                          enableBypassSurveyValidation: fullUserData.EnableBypassSurveyValidation ?? fullUserData.enableBypassSurveyValidation ?? false,
                          enableMultiSurveyExperience: fullUserData.EnableMultiSurveyExperience ?? fullUserData.enableMultiSurveyExperience ?? false
                        } : u)
                      );
                      setIsEditingUser(true);
                    } catch (error) {
                      console.error('Failed to load user details:', error);
                      showToast({ variant: 'error', message: 'Failed to load user details' });
                    }
                  }}
                  disabled={!selectedUserId}
                >
                  Edit Selected User
                </Button>
              </>
            ) : (
              <EditUserForm
                selectedUser={selectedUser}
                hospices={hospices}
                roles={roles}
                onSave={handleSaveUser}
                onCancel={() => {
                  setIsEditingUser(false);
                  setSelectedUserId(null);
                }}
                isLoading={apiLoading}
              />
            )}
          </div>
        </div>
      </div>

      {/* Resend New User Email */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="card p-3 h-100">
            <h4 style={{ marginTop: 0, color: "#F16D09" }}>Resend New User Email</h4>
            <p className="text-muted">
              If a user has not received their account setup email, you can resend it here.
            </p>

            <div className="mb-3">
              <label className="form-label">
                <span className="form-label">Find User:</span>
              </label>
              <div className="position-relative" style={{ maxWidth: 600 }}>
                <input
                  ref={resendSuggestionsRef}
                  className="form-control"
                  placeholder="Start typing name or email..."
                  value={resendSearchText}
                  onChange={(e) => {
                    setResendSearchText(e.target.value);
                    setResendShowSuggestions(true);
                    setResendSelectedUserId("");
                  }}
                  onFocus={() => setResendShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setResendShowSuggestions(false), 150)}
                  onKeyDown={handleResendSearchKeyDown}
                  aria-autocomplete="list"
                  aria-expanded={resendShowSuggestions}
                />

                {resendShowSuggestions && resendFilteredUsers.length > 0 && (
                  <ul
                    className="list-group position-absolute"
                    style={{ zIndex: 1200, width: "100%", maxHeight: 240, overflowY: "auto" }}
                  >
                    {resendFilteredUsers.map((u, idx) => (
                      <li
                        key={u.id}
                        className={`list-group-item list-group-item-action ${idx === resendHighlightedIndex ? "active" : ""}`}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onMouseEnter={() => setResendHighlightedIndex(idx)}
                        onClick={() => onResendSuggestionClick(u)}
                      >
                        <div>
                          <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>{u.firstName} {u.lastName}</span>
                        </div>
                        <div className="text-muted" style={{ fontSize: ".85rem" }}>
                          {u.email} 
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="d-flex gap-3">
              <Button
                type="button"
                onClick={resendNewUserEmail}
                disabled={isSaving || apiLoading || !resendSelectedUserId}
              >
                {isSaving ? "Sending…" : "Resend New User Email"}
              </Button>
              <Button
                type="button"
                onClick={() => { setResendSelectedUserId(""); setResendSearchText(""); }}
                variant="secondary"
              >
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Archive User */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="card p-3 h-100">
            <h4 style={{ marginTop: 0, color: "#F16D09" }}>Archive User</h4>
            <p className="text-muted">
              Archive a user so they are no longer active. Their data will be preserved.
            </p>

            <div className="mb-3">
              <label className="form-label">
                <span className="form-label">Archive User:</span>
              </label>
              <div className="position-relative" style={{ maxWidth: 600 }}>
                <input
                  ref={archiveSuggestionsRef}
                  className="form-control"
                  placeholder="Start typing name or email..."
                  value={archiveSearchText}
                  onChange={(e) => {
                    setArchiveSearchText(e.target.value);
                    setArchiveShowSuggestions(true);
                    setArchiveUserId("");
                  }}
                  onFocus={() => setArchiveShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setArchiveShowSuggestions(false), 150)}
                  onKeyDown={handleArchiveSearchKeyDown}
                  aria-autocomplete="list"
                  aria-expanded={archiveShowSuggestions}
                />

                {archiveShowSuggestions && archiveFilteredUsers.length > 0 && (
                  <ul
                    className="list-group position-absolute"
                    style={{ zIndex: 1200, width: "100%", maxHeight: 240, overflowY: "auto" }}
                  >
                    {archiveFilteredUsers.map((u, idx) => (
                      <li
                        key={u.id}
                        className={`list-group-item list-group-item-action ${idx === archiveHighlightedIndex ? "active" : ""}`}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onMouseEnter={() => setArchiveHighlightedIndex(idx)}
                        onClick={() => onArchiveSuggestionClick(u)}
                      >
                        <div>
                          <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>{u.firstName} {u.lastName}</span>
                        </div>
                        <div className="text-muted" style={{ fontSize: ".85rem" }}>
                          {u.email}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {archiveSelectedUser ? (
              <div className="d-flex align-items-center justify-content-between" style={{ maxWidth: 600 }}>
                <div>
                  <div>
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                      {archiveSelectedUser.firstName} {archiveSelectedUser.lastName}
                    </span>
                  </div>
                  <div style={{ color: "#666" }}>{archiveSelectedUser.email}</div>
                </div>
                <div>
                  {!(pendingDeleteUserId && String(pendingDeleteUserId) === String(archiveSelectedUser.id)) && (
                    <Button type="button" onClick={() => startDelete(archiveSelectedUser.id)} variant="danger">
                      Archive User
                    </Button>
                  )}
                  {pendingDeleteUserId && String(pendingDeleteUserId) === String(archiveSelectedUser.id) && (
                    <div className="d-flex gap-2">
                      <Button type="button" onClick={() => performDelete(pendingDeleteUserId)} variant="danger">
                        Yes, archive
                      </Button>
                      <Button type="button" onClick={() => setPendingDeleteUserId(null)} variant="secondary">
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ color: "#666" }}>No user selected</div>
            )}
          </div>
        </div>
      </div>

       {/* Track User */}

      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="card p-3 h-100">
            <h4 style={{ marginTop: 0, color: "#F16D09" }}>Track User</h4>
            <p className="text-muted">Select an individual user from the list to track their details.</p>

            {!isTrackingUser ? (
              <>
                <div className="mb-3">
                  <label style={{ color: "#2e1719" }} className="form-label">Select User:</label>
                  <SearchableSelect
                    options={users}
                    value={selectedUserId || ''}
                    onChange={(userId) => setSelectedUserId(userId)}
                    placeholder="Search or select..."
                    getLabel={(user) => `${user.firstName} ${user.lastName} (${user.email})`}
                    getValue={(user) => user.id}
                    showToast={showToast}
                  />
                </div>

                <Button
                  onClick={async () => {
                    setLoading(true);
                    const data = await secureHelpers.get(`/api/Members/${selectedUserId}/Audit`);
                    console.log('Audit logs for user ID', selectedUserId, ':', data);
                    setIsTrackingUser(true);
                    setAuditLogs(data);
                    setLoading(false);
                  }}
                >
                  Track Selected User
                </Button>
                {loading && <Spinner animation="border" size="md" />}
              </>
            ) : (
              <TrackUserDashboard
                selectedUser={selectedUser}
                auditLogs={auditLogs}
                onCancel={() => {
                  setIsTrackingUser(false);
                  setSelectedUserId(null);
                }}
              onLoaded={handleChildLoaded}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
